import { Icon, IconName } from '@release/ui/Icon';
import { textVariant, Typography } from '@release/ui/Typography';

import clsx from 'clsx';
import {
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { tv } from 'tailwind-variants';

export const inputTV = tv({
  extend: textVariant,
  base: 'box-border h-[42px] w-[352px] rounded-md border border-sem-l-divider-medium bg-sem-l-surf-primary px-3 py-2 outline-none ring-sem-l-divider-accent transition-all duration-300 ease-in-out placeholder:text-sem-l-text-low hover:border-sem-l-divider-accent hover:ring-1 focus:border-sem-l-divider-accent focus:ring-1 disabled:pointer-events-none disabled:border-sem-l-divider-medium-disabled disabled:bg-sem-l-surf-primary-disabled disabled:text-sem-l-text-high-disabled',
  variants: {
    isError: {
      true: 'border-sem-d-surf-error bg-sem-l-surf-error ring-sem-d-surf-error hover:border-sem-d-surf-error hover:ring-1 focus:border-sem-d-surf-error ',
    },
    hasIcon: {
      true: 'pl-9',
    },
    hasSuffix: {
      true: 'text-right',
    },
    fill: {
      true: 'w-full',
    },
    hasTrailingIcon: {
      true: 'pr-9',
    },
  },
});

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  isError?: boolean;
  // 右端に付与される単位など(例: 円) これがある場合、右揃えになる
  suffix?: string;
  fill?: boolean;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  onTrailingIconClick?: () => void;
  formatPrice?: boolean;
  onBlurPlaceholder?: string;
  onFocusPlaceholder?: string;
}

const priceFormatter = new Intl.NumberFormat('ja-JP');

/**
 * 全角英数記号（U+FF01-FF5E）を半角に変換する。
 * `@release/shared/utils/toHalfWidthAscii` と同一実装。@release/ui は @release/shared に
 * 依存できない（shared → ui の依存方向のため循環依存になる）ので、このファイル内に複製している。
 */
const toHalfWidthAscii = (s: string): string =>
  s.replace(/[！-～]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

const formatPriceWithComma = (price: string | number | readonly string[] | undefined): string => {
  let numPrice: number;
  if (typeof price === 'string') {
    // 全角数字（IME入力等）は半角変換してから抽出する。変換前に抽出すると全角数字が
    // 非数字扱いで削除され、入力が消える不具合になるため。
    numPrice = parseInt(toHalfWidthAscii(price).replace(/[^\d]+/g, ''), 10);
  } else if (typeof price === 'number') {
    numPrice = price;
  } else {
    return '';
  }

  if (Number.isNaN(numPrice)) {
    return '';
  }
  return priceFormatter.format(numPrice);
};

const TextFieldWithRef: ForwardRefRenderFunction<HTMLInputElement, TextFieldProps> = (
  {
    isError = false,
    suffix = false,
    fill,
    className,
    leadingIcon,
    trailingIcon,
    onTrailingIconClick,
    formatPrice,
    onBlurPlaceholder,
    onFocusPlaceholder,
    ...textFieldProps
  },
  ref
) => {
  const { autoComplete = 'off', value, onChange, ...textFieldPropsRest } = textFieldProps;

  const [formattedValue, setFormattedValue] = useState(formatPrice ? formatPriceWithComma(value) : value);
  const innerInputRef = useRef<HTMLInputElement>(null);
  const lastCursorPosRef = useRef<{ start: number; end: number } | null>(null);

  // forwardedRefと内部のrefを同期させる
  useImperativeHandle(ref, () => innerInputRef.current as HTMLInputElement);

  useEffect(() => {
    setFormattedValue(formatPrice ? formatPriceWithComma(value) : value);
  }, [value, formatPrice]);

  // これをしている理由は、マニュアルでvalueの値を変更するとカーソルの位置がリセットされてしまうから
  useEffect(() => {
    if (lastCursorPosRef.current) {
      innerInputRef.current?.setSelectionRange(lastCursorPosRef.current.start, lastCursorPosRef.current.end);
    }
  }, [formattedValue]);

  // typeがnumberの場合、ホイールイベントで値が変わらないようにする
  useEffect(() => {
    const inputElement = innerInputRef.current;
    if (inputElement && textFieldPropsRest.type === 'number') {
      const handleWheel = (event: WheelEvent) => {
        // スクロールによる増減を防ぐ
        event.preventDefault();
      };
      inputElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        inputElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [textFieldPropsRest.type]);

  // suffixの幅を動的に取得する
  const [suffixWidth, setSuffixWidth] = useState<number | null>(null);
  const suffixRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (suffixRef.current) {
      setSuffixWidth(suffixRef.current.getBoundingClientRect().width);
    }
  }, [suffixRef, suffix]);

  const style = inputTV({
    size: 'cat',
    weight: 'regular',
    isError,
    fill,
    hasSuffix: Boolean(suffix),
    hasIcon: Boolean(leadingIcon),
    hasTrailingIcon: Boolean(trailingIcon),
    className,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formatPrice) {
      setFormattedValue(e.target.value);
      onChange?.(e);
      return;
    }

    // 金額にカンマを挿入する処理
    const startPos = e.target.selectionStart;
    const endPos = e.target.selectionEnd;
    // 料金モードの場合、コンマを差し込む
    const prevValue = e.target.value;
    const nextValue = formatPriceWithComma(e.target.value);
    setFormattedValue(nextValue);

    // カーソル位置を保持
    // 一度refに保存する理由は、setFormattedValueの変更を待たないと結局その時にカーソルの位置がリセットされてしまうため
    const offset = prevValue.length - nextValue.length;
    if (startPos && endPos) {
      lastCursorPosRef.current = { start: startPos - offset, end: endPos - offset };
    }

    onChange?.({
      ...e,
      target: {
        ...e.target,
        value: toHalfWidthAscii(e.target.value).replace(/[^\d]+/g, ''),
      },
    });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlurPlaceholder) {
      e.target.placeholder = onBlurPlaceholder;
    }
    textFieldPropsRest.onBlur?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onFocusPlaceholder) {
      e.target.placeholder = onFocusPlaceholder;
    }
    textFieldPropsRest.onFocus?.(e);
  };

  const inputMode = formatPrice ? (textFieldPropsRest.inputMode ?? 'numeric') : textFieldPropsRest.inputMode;

  if (suffix) {
    // suffixとtrailingIconの位置を動的に計算
    const suffixRight = trailingIcon ? 40 : 12;
    const trailingIconRight = 12;
    const gap = suffixRight - trailingIconRight;
    const inputPaddingRight = suffixWidth ? 8 + suffixWidth + gap + 12 : undefined;

    return (
      <div className={clsx('relative flex flex-row items-center', fill && 'w-full')}>
        {leadingIcon && (
          <Icon
            name={leadingIcon}
            size="16"
            className={clsx(
              'absolute left-3',
              textFieldProps.disabled ? 'fill-sem-l-icon-high-disabled' : 'fill-sem-l-icon-high'
            )}
          />
        )}
        <input
          {...textFieldPropsRest}
          value={formattedValue}
          onChange={handleChange}
          className={style}
          //
          style={{
            opacity: suffixWidth ? 1 : 0,
            paddingRight: inputPaddingRight,
          }}
          autoComplete={autoComplete}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={innerInputRef}
          inputMode={inputMode}
        />
        <Typography
          // ちらつき防止
          className={clsx(
            'absolute inline-block',
            suffixWidth ? 'opacity-100' : 'opacity-0',
            textFieldProps.disabled ? 'text-sem-l-text-high-disabled' : 'text-sem-l-text-high',
            // trailingIconがある場合は、trailingIconの左側に配置
            trailingIcon ? 'right-10' : 'right-3'
          )}
          ref={suffixRef}
        >
          {suffix}
        </Typography>
        {trailingIcon && (
          <Icon
            name={trailingIcon}
            size="16"
            className={clsx(
              'absolute right-3 cursor-pointer',
              textFieldProps.disabled ? 'fill-sem-l-icon-high-disabled' : 'fill-sem-l-icon-high'
            )}
            // クリック処理がある場合のみ blur を抑止（TimePicker 等は onBlur で確定するため常時抑止は不可）
            onMouseDown={onTrailingIconClick ? (e) => e.preventDefault() : undefined}
            onClick={onTrailingIconClick}
          />
        )}
      </div>
    );
  } else {
    return (
      <div className={clsx('relative flex flex-row items-center', fill && 'w-full')}>
        {leadingIcon && (
          <Icon name={leadingIcon} size="20" className={clsx('absolute left-3', 'fill-sem-l-icon-medium')} />
        )}
        <input
          {...textFieldPropsRest}
          onChange={handleChange}
          value={formattedValue}
          className={style}
          autoComplete={autoComplete}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={innerInputRef}
        />
        {trailingIcon && (
          <Icon
            name={trailingIcon}
            size="16"
            className={clsx(
              'absolute right-3 cursor-pointer',
              textFieldProps.disabled ? 'fill-sem-l-icon-high-disabled' : 'fill-sem-l-icon-high'
            )}
            // クリック処理がある場合のみ blur を抑止（TimePicker 等は onBlur で確定するため常時抑止は不可）
            onMouseDown={onTrailingIconClick ? (e) => e.preventDefault() : undefined}
            onClick={onTrailingIconClick}
          />
        )}
      </div>
    );
  }
};

export const TextField = forwardRef(TextFieldWithRef);
